import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Locks down an article body against casual copying + long-press save +
 * drag-save-image, and tiles the Outlaw Media wordmark across it in plain
 * white so any screenshot that DOES escape is obviously ours.
 *
 * Honest limitations: the web has no API to prevent OS-level screenshots
 * (Cmd+Shift+3, Print Screen, phone hardware buttons). This component is a
 * deterrent + attribution layer, not perfect DRM.
 */
const ContentProtection = ({ disabled, children }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (disabled) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    let lastToast = 0;
    const flash = (msg) => {
      const now = Date.now();
      if (now - lastToast < 1500) return; // debounce spam
      lastToast = now;
      toast.error(msg, { duration: 2200 });
    };

    const onCopy = (e) => {
      e.preventDefault();
      // Overwrite the clipboard with an attribution notice instead of the
      // article text - so if the reader manages to force a copy via a
      // browser extension, they still paste a Outlaw Media ID card.
      if (e.clipboardData) {
        e.clipboardData.setData(
          'text/plain',
          `Copying is disabled. Read this story on Outlaw Media: ${window.location.href}`,
        );
      }
      flash('Copying is disabled - © Outlaw Media');
    };

    const onCtx = (e) => {
      e.preventDefault();
      flash('Right-click is disabled on articles');
    };

    const onDragStart = (e) => {
      e.preventDefault();
    };

    const onKey = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const k = (e.key || '').toLowerCase();
      // Ctrl/Cmd + A / C / X / S / U / P / Shift-I / Shift-C / Shift-J
      if (['a', 'c', 'x', 's', 'u', 'p'].includes(k)) {
        e.preventDefault();
        flash('That shortcut is disabled on articles');
      } else if (e.shiftKey && ['i', 'c', 'j'].includes(k)) {
        e.preventDefault();
        flash('Dev tools blocked on articles');
      }
    };

    const onSelectStart = () => {
      // Called on selection attempts - silent, CSS already prevents it
      // but Ctrl+A can bypass on some browsers. Clear immediately.
      try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
    };

    node.addEventListener('copy', onCopy);
    node.addEventListener('cut', onCopy);
    node.addEventListener('contextmenu', onCtx);
    node.addEventListener('dragstart', onDragStart);
    node.addEventListener('selectstart', onSelectStart);
    window.addEventListener('keydown', onKey);

    return () => {
      node.removeEventListener('copy', onCopy);
      node.removeEventListener('cut', onCopy);
      node.removeEventListener('contextmenu', onCtx);
      node.removeEventListener('dragstart', onDragStart);
      node.removeEventListener('selectstart', onSelectStart);
      window.removeEventListener('keydown', onKey);
    };
  }, [disabled]);

  if (disabled) return <>{children}</>;

  return (
    <div
      ref={ref}
      data-testid="content-protection"
      className="outlaw-protect relative overflow-hidden"
      style={{
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <style>{`
        /* Kill image drag-save + iOS long-press callout. */
        .outlaw-protect img,
        .outlaw-protect picture,
        .outlaw-protect figure { -webkit-user-drag: none; -webkit-touch-callout: none; }
        .outlaw-protect { print-color-adjust: exact; }
        @media print {
          .outlaw-protect * { visibility: hidden !important; }
          .outlaw-protect::before {
            visibility: visible; content: 'Printing is disabled on Outlaw Media articles.';
            font-family: Arial, sans-serif; color: #000; display: block; padding: 40px;
          }
        }
      `}</style>
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export default ContentProtection;
